// The two writes the page makes to an item's fulfillment target, kept pure so the
// item editor and the fulfillment section patch a line's config the same way.
//
// The target lives on the LINE (`config.fulfillment_target`), one per item, read
// back through the shared resolver. An assembly item once carried its remap on
// its kit (`assembly.fulfillment_sku` + snapshot); that legacy pair is still read
// by `configFulfillmentTarget`, so every write here clears it in the same patch.
// Left behind, a stale remap would outrank the default the customer just chose.

import {
  ASSEMBLY_ITEM_CONFIG_KEY,
  FULFILLMENT_TARGET_CONFIG_KEY,
  configFulfillmentTarget,
  type AssemblySnapshot,
  type LineFulfillmentTarget,
} from 'shared';

function kitOf(config: Record<string, unknown>): AssemblySnapshot | undefined {
  const raw = config[ASSEMBLY_ITEM_CONFIG_KEY];
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  return raw as AssemblySnapshot;
}

// `undefined` is the item's default — into its kit for a print a kit on the order
// consumes, its own kit's SKU for an assembly — so it removes the key rather than
// storing a choice that restates the default.
export function withLineFulfillmentTarget<T extends Record<string, unknown>>(
  config: T,
  target: LineFulfillmentTarget | undefined,
): T {
  const out: Record<string, unknown> = { ...config };
  if (target) out[FULFILLMENT_TARGET_CONFIG_KEY] = target;
  else delete out[FULFILLMENT_TARGET_CONFIG_KEY];
  const kit = kitOf(config);
  if (kit && ('fulfillment_sku' in kit || 'fulfillment_sku_snapshot' in kit)) {
    const { fulfillment_sku: _sku, fulfillment_sku_snapshot: _snap, ...rest } = kit;
    out[ASSEMBLY_ITEM_CONFIG_KEY] = rest;
  }
  return out as T;
}

// Picking a kit makes a target pointing at that kit's own SKU no remap at all:
// the kit's own SKU is already where its finished kits go. So when the kit pick
// changes onto the SKU the item was mapped to, the mapping is dropped.
export function withKitPickReconciled<T extends Record<string, unknown>>(
  before: Record<string, unknown>,
  after: T,
): T {
  const prevKit = kitOf(before);
  const nextKit = kitOf(after);
  if (!nextKit || prevKit?.bom_id === nextKit.bom_id) return after;
  const kitSku = nextKit.bom_snapshot?.bom_number ?? nextKit.bom_id;
  const target = configFulfillmentTarget(after);
  if (!kitSku || target?.choice !== 'existing' || target.sku !== kitSku) return after;
  return withLineFulfillmentTarget(after, undefined);
}
